import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Empty, Tag, Input, Switch } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import TableColumnMessageType from "../../components/table/TableColumnMessageType";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { MessageTypeInterface } from "../../../interfaces/MessageType.interface";
// CUSTOM HOOKS
import useGetAllMessagesTypes from "../../../customHooks/GET_requests/useGetAllMessagesTypes";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";

function MessageTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Column, ColumnGroup } = Table;
  const CONTENT_TYPE: { [key: number]: string } = {
    1: "One Hot Message",
    2: "Bit Mask Message",
  };

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    showSizeChanger: boolean;
    pageSizeOptions: string[];
  }>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { messagesTypes } = useGetAllMessagesTypes();
  const {
    messages,
    isLoadingGetAllMessages,
    isErrorGetAllMessages,
    totalRowsGetAllMessages,
    errorMessageGetAllMessages,
  } = useGetAllMessages({ deviceType: "", messageTypeId: "" });

  // ************************************************ */
  // HANDLERS *************************************** */
  const getMessageTypeName = (id: number) => {
    if (messagesTypes) {
      const foundType = messagesTypes.find((mt: MessageTypeInterface) => mt.id === id);
      return foundType ? foundType.name : "";
    }
    return "";
  };

  const handleTableChange = (paginationUpdate: {
    current?: number;
    pageSize?: number;
    total?: number;
  }) => {
    setPagination({
      ...pagination,
      current: paginationUpdate.current || pagination.current,
      pageSize: paginationUpdate.pageSize || pagination.pageSize,
    });
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div>
        {hasPermission(Permission.PermissionNotDefined, permissionSet ?? []) ? (
          <Table
            locale={{
              emptyText: (
                <TableNoDataBox
                  isError={isErrorGetAllMessages}
                  errorMessage={errorMessageGetAllMessages}
                  noItemsFound="No messages found"
                />
              ),
            }}
            rowClassName={(record, index) => getTableRowClass(index, theme)}
            dataSource={messages}
            pagination={{ ...pagination, total: totalRowsGetAllMessages }}
            onChange={handleTableChange}
            size="small"
            className="my-custom-table"
            loading={isLoadingGetAllMessages}
            footer={() => (
              <div className="generalStyles__flexEnd">
                <Tag>{totalRowsGetAllMessages} Rows</Tag>
              </div>
            )}
          >
            {/** ID */}
            <ColumnGroup title="ID">
              <Column
                title="ID"
                dataIndex="id"
                key="id"
                render={(text: string) => (
                  <div>
                    <span>{text}</span>
                  </div>
                )}
              />
            </ColumnGroup>
            {/** MESSAGE TYPES */}
            <ColumnGroup title="Message Type">
              <Column
                dataIndex="message_type_id"
                key="message_type_id"
                render={(text: number) => (
                  <TableColumnMessageType messageType={getMessageTypeName(text)} />
                )}
              />
            </ColumnGroup>
            {/** DEVICE TYPE */}
            <ColumnGroup title="Device Type">
              <Column
                dataIndex="device_type"
                key="device_type"
                render={(text: string) => (
                  <div>
                    {text === "ELEVATOR" && (
                      <Tag style={{ width: "125px", textAlign: "center" }} color="blue">
                        {text}
                      </Tag>
                    )}
                    {text === "ESCALATOR" && (
                      <Tag style={{ width: "125px", textAlign: "center" }} color="gold">
                        {text}
                      </Tag>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** CONTENT TYPE */}
            <ColumnGroup title="Content Type">
              <Column
                dataIndex="message_content_type"
                key="message_content_type"
                render={(text: number) => (
                  <Input size="small" disabled value={CONTENT_TYPE[text]} />
                )}
              />
            </ColumnGroup>
            {/** ENUM ID */}
            <ColumnGroup title="Enum ID">
              <Column
                width={120}
                dataIndex="enum_id"
                key="enum_id"
                render={(text: number) => <div>{text}</div>}
              />
            </ColumnGroup>
            {/** SHORT MESSAGE */}
            <ColumnGroup title="Short Message">
              <Column
                width={200}
                dataIndex="short_message"
                key="short_message"
                render={(text: string) => <>{text}</>}
              />
            </ColumnGroup>
            {/** DESCRIPTION */}
            <ColumnGroup title="Description">
              <Column
                width={200}
                dataIndex="description"
                key="description"
                render={(text: string) => <div>{text || "--"}</div>}
              />
            </ColumnGroup>
            {/** POSITION */}
            <ColumnGroup title="Position">
              <Column
                dataIndex="position"
                key="position"
                render={(text: boolean) => (
                  <Switch
                    disabled
                    checkedChildren={t("configuration.message.yes")}
                    unCheckedChildren={t("configuration.message.no")}
                    checked={text}
                  />
                )}
              />
            </ColumnGroup>
          </Table>
        ) : (
          <div className="generalStyles__noAccessToListTable">
            <Empty
              description={t("organizationManagement.listNotAvailableOrganizations")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageTable;
